import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAgent } from "@/context/AgentContext";
import { ORB_IDLE_URL, ORB_ACTIVE_URL } from "@/lib/agentOrb";

/**
 * FloatingAgent — draggable cyan orb rendered globally inside <Protected>.
 *
 * Responsibilities:
 *  - Renders the orb (idle / listening) with float + glow animations.
 *  - Handles pointer-based drag (mouse + touch), clamped to the *device frame*
 *    on desktop and to the safe viewport area (accounting for header + bottom
 *    tab bar + the Emergent watermark) on mobile.
 *  - Distinguishes CLICK (< 5px movement) from DRAG — click opens the dock.
 *  - Persists the user's preferred position in localStorage so it survives
 *    reloads; re-clamps on mount if the viewport shrank.
 *  - On route `/ia` the orb dims itself (the mic on that page already is the
 *    main surface) so it doesn't duplicate controls — still clickable.
 */
const POSITION_KEY = "nexagenda:agent:pos:v1";
const CLICK_THRESHOLD_PX = 5;
const ORB_SIZE = 72;
const ORB_SIZE_LISTENING = 80;

// Fallback margins when we can't find the device frame (edge cases).
const MARGIN_TOP_MOBILE = 80;
const MARGIN_BOTTOM_MOBILE = 140;
const MARGIN_X_MOBILE = 8;

function loadSavedPosition() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(POSITION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.x === "number" &&
      typeof parsed.y === "number" &&
      Number.isFinite(parsed.x) &&
      Number.isFinite(parsed.y)
    ) {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function savePosition(p) {
  try {
    window.localStorage.setItem(POSITION_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

/** Returns the bounding rect the orb must stay inside. */
function getContainerRect() {
  if (typeof window === "undefined") {
    return { left: 0, top: 0, right: 0, bottom: 0 };
  }
  const frame = document.querySelector('[data-testid="device-frame"]');
  if (frame) {
    const r = frame.getBoundingClientRect();
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    if (isDesktop) {
      return {
        left: r.left + 8,
        top: r.top + 80, // below header
        right: r.right - 8,
        bottom: r.bottom - 100, // above bottom tab bar
      };
    }
    // Mobile: the frame is full width; respect safe areas + tab bar offset.
    return {
      left: r.left + MARGIN_X_MOBILE,
      top: r.top + MARGIN_TOP_MOBILE,
      right: r.right - MARGIN_X_MOBILE,
      bottom: r.bottom - MARGIN_BOTTOM_MOBILE,
    };
  }
  return {
    left: MARGIN_X_MOBILE,
    top: MARGIN_TOP_MOBILE,
    right: window.innerWidth - MARGIN_X_MOBILE,
    bottom: window.innerHeight - MARGIN_BOTTOM_MOBILE,
  };
}

function clamp(pos, size) {
  const rect = getContainerRect();
  const minX = rect.left;
  const maxX = Math.max(rect.left, rect.right - size);
  const minY = rect.top;
  const maxY = Math.max(rect.top, rect.bottom - size);
  return {
    x: Math.min(Math.max(pos.x, minX), maxX),
    y: Math.min(Math.max(pos.y, minY), maxY),
  };
}

function getDefaultPosition(size) {
  const rect = getContainerRect();
  return {
    x: Math.max(rect.left, rect.right - size - 16),
    y: Math.max(rect.top, rect.bottom - size - 16),
  };
}

export default function FloatingAgent() {
  const { pathname } = useLocation();
  const { openDock, dockOpen, closeDock } = useAgent();

  const [position, setPosition] = useState(() => null);
  const [dragging, setDragging] = useState(false);
  const [imgError, setImgError] = useState(false);
  const orbRef = useRef(null);
  const dragStateRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    moved: false,
    pointerId: null,
  });

  const isListening = false; // reserved — dock-driven listening visual handled inside dock mic.
  const size = isListening ? ORB_SIZE_LISTENING : ORB_SIZE;
  const dimmedOnIA = pathname === "/ia" && !dockOpen;

  // Initial position — respect saved, clamp to current viewport.
  useEffect(() => {
    const saved = loadSavedPosition();
    const base = saved || getDefaultPosition(size);
    setPosition(clamp(base, size));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-clamp on viewport resize so the orb never ends up off-screen.
  useEffect(() => {
    const onResize = () => {
      setPosition((prev) => (prev ? clamp(prev, size) : prev));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [size]);

  const onPointerDown = useCallback(
    (e) => {
      if (!orbRef.current) return;
      e.preventDefault();
      const rect = orbRef.current.getBoundingClientRect();
      dragStateRef.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        moved: false,
        pointerId: e.pointerId,
      };
      try {
        orbRef.current.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    [],
  );

  const onPointerMove = useCallback(
    (e) => {
      const s = dragStateRef.current;
      if (!s.active) return;
      const dx = Math.abs(e.clientX - s.startX);
      const dy = Math.abs(e.clientY - s.startY);
      if (!s.moved && (dx > CLICK_THRESHOLD_PX || dy > CLICK_THRESHOLD_PX)) {
        s.moved = true;
        setDragging(true);
      }
      if (s.moved) {
        const next = clamp(
          { x: e.clientX - s.offsetX, y: e.clientY - s.offsetY },
          size,
        );
        setPosition(next);
      }
    },
    [size],
  );

  const onPointerUp = useCallback(
    (e) => {
      const s = dragStateRef.current;
      if (!s.active) return;
      s.active = false;
      try {
        orbRef.current?.releasePointerCapture?.(s.pointerId);
      } catch {
        /* ignore */
      }
      if (s.moved) {
        setDragging(false);
        setPosition((p) => {
          if (p) savePosition(p);
          return p;
        });
      } else {
        // Clean click — open/close dock. Pass the orb rect so the dock can
        // anchor + flip-up as needed.
        const rect = orbRef.current?.getBoundingClientRect();
        if (dockOpen) {
          closeDock();
        } else if (rect) {
          openDock({
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
            height: rect.height,
            centerX: rect.left + rect.width / 2,
          });
        }
      }
    },
    [openDock, closeDock, dockOpen],
  );

  if (!position) return null;

  const orbUrl = isListening ? ORB_ACTIVE_URL : ORB_IDLE_URL;

  return (
    <div
      ref={orbRef}
      data-testid="floating-agent"
      data-dragging={dragging ? "true" : "false"}
      data-open={dockOpen ? "true" : "false"}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size}px`,
        height: `${size}px`,
        zIndex: 8500,
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        cursor: dragging ? "grabbing" : "grab",
        transition: dragging ? "none" : "width 200ms ease, height 200ms ease, opacity 300ms ease",
        opacity: dimmedOnIA ? 0.45 : 1,
      }}
      className={`nx-agent-orb ${dockOpen ? "nx-agent-orb--open" : ""} ${dragging ? "nx-agent-orb--dragging" : ""}`}
      role="button"
      aria-label="Asistente NexAgenda"
      aria-expanded={dockOpen}
    >
      {/* Outer pulse ring (idle breath) */}
      <span className="nx-agent-orb__ring" aria-hidden="true" />

      {imgError ? (
        // Fallback: SVG radial gradient when the JPEGs fail to load
        <svg
          viewBox="0 0 80 80"
          width="100%"
          height="100%"
          aria-hidden="true"
          style={{ filter: "drop-shadow(0 0 18px rgba(0,229,255,0.55))" }}
        >
          <defs>
            <radialGradient id="nx-orb-fallback" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#A5F3FC" stopOpacity="1" />
              <stop offset="45%" stopColor="#00E5FF" stopOpacity="0.9" />
              <stop offset="85%" stopColor="#0891B2" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#0A0E1A" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="40" cy="40" r="32" fill="url(#nx-orb-fallback)" />
        </svg>
      ) : (
        <img
          src={orbUrl}
          alt=""
          draggable={false}
          onError={() => setImgError(true)}
          className="nx-agent-orb__img"
        />
      )}
    </div>
  );
}
